import * as React from "react";
import { CalendarRange, ChevronDown, ChevronLeft, ChevronRight, Globe } from "lucide-react";

import { cn } from "@/lib/utils";

/** A bookable time slot — start/end ISO. Matches both PublicBookingSlot and BookingPreviewSlot. */
export interface CalendarSlot {
  start: string;
  end: string;
}

/** A booked (taken) slot, shown only on the host's own calendar — carries the invitee for context. */
export interface BookedSlot {
  start: string;
  end: string;
  label?: string;
}

/**
 * A slot the builder preview dropped because it clashes with one of the host's meetings — shown as a
 * disabled conflict marker so the host sees WHY a time isn't on offer. Builder previews only; the
 * public invitee calendar never receives these.
 */
export interface BlockedSlot {
  start: string;
  end: string;
  /** Start (ISO) of the overlapping meeting, for the marker's tooltip. */
  conflictAt?: string | null;
}

const DOW_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DOW_MINI = ["M", "T", "W", "T", "F", "S", "S"];

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function mondayOf(d: Date): Date {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  out.setDate(out.getDate() - ((out.getDay() + 6) % 7));
  return out;
}
function fmtTime(iso: string, tz?: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    ...(tz ? { timeZone: tz } : {}),
  });
}
/** Calendar date (YYYY-MM-DD) an instant falls on IN the given zone — so slots bucket to the right
 *  day once the viewer changes timezone. Falls back to the browser's local day when tz is unset. */
function ymdz(iso: string, tz?: string): string {
  if (!tz) return ymd(new Date(iso));
  try {
    const p = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date(iso));
    const get = (t: string) => p.find((x) => x.type === t)?.value ?? "";
    return `${get("year")}-${get("month")}-${get("day")}`;
  } catch {
    return ymd(new Date(iso));
  }
}

/** A curated timezone list for the invitee picker — the viewer's own zone is added on top at runtime. */
const ZONES: { id: string; name: string }[] = [
  { id: "UTC", name: "Coordinated Universal Time" },
  { id: "Europe/London", name: "London, Dublin, Lisbon" },
  { id: "Europe/Paris", name: "Paris, Berlin, Madrid, Rome" },
  { id: "Europe/Copenhagen", name: "Copenhagen, Brussels, Amsterdam" },
  { id: "Europe/Athens", name: "Athens, Helsinki, Bucharest" },
  { id: "Europe/Moscow", name: "Moscow, Istanbul" },
  { id: "Asia/Dubai", name: "Dubai, Abu Dhabi" },
  { id: "Asia/Kolkata", name: "Chennai, Kolkata, Mumbai" },
  { id: "Asia/Bangkok", name: "Bangkok, Jakarta, Hanoi" },
  { id: "Asia/Singapore", name: "Singapore, Hong Kong, Beijing" },
  { id: "Asia/Tokyo", name: "Tokyo, Seoul" },
  { id: "Australia/Sydney", name: "Sydney, Melbourne" },
  { id: "Pacific/Auckland", name: "Auckland, Wellington" },
  { id: "America/Sao_Paulo", name: "São Paulo, Buenos Aires" },
  { id: "America/New_York", name: "Eastern Time (US & Canada)" },
  { id: "America/Chicago", name: "Central Time (US & Canada)" },
  { id: "America/Denver", name: "Mountain Time (US & Canada)" },
  { id: "America/Los_Angeles", name: "Pacific Time (US & Canada)" },
  { id: "America/Anchorage", name: "Alaska" },
  { id: "Pacific/Honolulu", name: "Hawaii" },
];

/** "GMT+2" for a zone right now (DST-correct), for the picker labels. */
function zoneOffset(id: string): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: id, timeZoneName: "shortOffset" }).formatToParts(
      new Date(),
    );
    return parts.find((p) => p.type === "timeZoneName")?.value ?? "";
  } catch {
    return "";
  }
}
function fmtDayLong(d: Date): string {
  return d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
}

type Selection = { kind: "day"; key: string } | { kind: "week"; mondayMs: number } | null;
type SlotMode = "cards" | "weeks";

/**
 * The booking calendar shared by the invitee link and the host preview: a compact month grid on
 * the left (round date cells, weekend-shaded, slot dots with a count) beside a slots focus panel
 * on the right. Click a day for its times; the ‹› button left of a week row loads the whole week.
 * The focus panel toggles between Cards (tap chips) and Weeks (a 7-day grid).
 *
 * <p>Lightweight and dependency-free (no FullCalendar) so it's safe on the anonymous invitee
 * bundle. Pass {@link onPick} for the interactive invitee flow; omit it for the read-only host
 * preview.</p>
 */
export function BookingCalendar({
  slots,
  slotMinutes,
  onPick,
  booked,
  blockedSlots,
  allowTimezoneChange,
  compact,
  viewMonth: viewMonthProp,
  onMonthChange,
  canGoPrev = true,
  canGoNext = true,
  loadingMonth = false,
  className,
}: {
  slots: CalendarSlot[];
  slotMinutes: number;
  /** Invitee flow: called when a slot is chosen. Omit → read-only preview (chips aren't clickable). */
  onPick?: (slot: CalendarSlot) => void;
  /** Host-only overlay: already-booked slots, shown as taken (never passed on the invitee link). */
  booked?: BookedSlot[];
  /** Builder preview only: slots dropped by a meeting clash, shown as disabled conflict markers. */
  blockedSlots?: BlockedSlot[];
  /** Let the viewer pick which timezone the times are shown in (a globe dropdown). Off → local only. */
  allowTimezoneChange?: boolean;
  /** Builder preview only: render smaller slot pills. Public invitee pages keep the tappable size. */
  compact?: boolean;
  /**
   * Paged invitee pages: the month on screen, owned by the caller so it can fetch that month.
   * Omit and the calendar owns it, which is what the builder previews do — they hold every slot
   * already and have nothing to fetch.
   */
  viewMonth?: Date;
  /** Called when the invitee pages. The caller loads the month and passes it back as `viewMonth`. */
  onMonthChange?: (month: Date) => void;
  /**
   * How far the arrows may go, from the link's availability bounds. Missing means unbounded, which
   * is the builder previews and any backend predating paging.
   *
   * <p>Never rendered, and no arrow explains why it is disabled. An invitee reaching the end simply
   * finds no further months — the same thing they would see from a host with nothing more free.
   * Saying more would name the host's plan.</p>
   */
  canGoPrev?: boolean;
  canGoNext?: boolean;
  /** The month on screen is still loading; the grid dims rather than flashing empty. */
  loadingMonth?: boolean;
  className?: string;
}) {
  const readOnly = !onPick;
  const bookedList = React.useMemo(() => booked ?? [], [booked]);
  const blockedList = React.useMemo(() => blockedSlots ?? [], [blockedSlots]);
  // The zone the times render in — defaults to the viewer's own; the picker (when enabled) changes it.
  const [tz, setTz] = React.useState<string>(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return "UTC";
    }
  });
  const [tzOpen, setTzOpen] = React.useState(false);
  const [tzQuery, setTzQuery] = React.useState("");

  // Group slots by local day (YYYY-MM-DD), each day's slots kept start-sorted.
  const byDay = React.useMemo(() => {
    const map = new Map<string, CalendarSlot[]>();
    for (const s of [...slots].sort((a, b) => a.start.localeCompare(b.start))) {
      const key = ymdz(s.start, tz);
      const arr = map.get(key);
      if (arr) arr.push(s);
      else map.set(key, [s]);
    }
    return map;
  }, [slots, tz]);

  const bookedByDay = React.useMemo(() => {
    const map = new Map<string, BookedSlot[]>();
    for (const s of [...bookedList].sort((a, b) => a.start.localeCompare(b.start))) {
      const key = ymdz(s.start, tz);
      const arr = map.get(key);
      if (arr) arr.push(s);
      else map.set(key, [s]);
    }
    return map;
  }, [bookedList, tz]);

  const blockedByDay = React.useMemo(() => {
    const map = new Map<string, BlockedSlot[]>();
    for (const s of [...blockedList].sort((a, b) => a.start.localeCompare(b.start))) {
      const key = ymdz(s.start, tz);
      const arr = map.get(key);
      if (arr) arr.push(s);
      else map.set(key, [s]);
    }
    return map;
  }, [blockedList, tz]);

  const earliest = React.useMemo(() => {
    const all = [...slots, ...bookedList, ...blockedList];
    return all.length ? new Date(all.reduce((a, b) => (a.start < b.start ? a : b)).start) : null;
  }, [slots, bookedList, blockedList]);

  // Uncontrolled by default (builder previews own nothing); controlled when the caller pages, so
  // the month on screen and the month being fetched cannot drift apart.
  const controlled = viewMonthProp !== undefined;
  const [ownMonth, setOwnMonth] = React.useState<Date>(() => startOfMonth(earliest ?? new Date()));
  // Held by value, not by identity. `startOfMonth(viewMonthProp)` mints a fresh Date on every
  // render, and this month is a dependency of the auto-select effect below — so an unmemoised one
  // made that effect fire on every render, re-running its setSel forever and stomping any week the
  // invitee had picked back to a single day.
  const viewMonthMs = controlled ? startOfMonth(viewMonthProp).getTime() : ownMonth.getTime();
  const viewMonth = React.useMemo(() => new Date(viewMonthMs), [viewMonthMs]);
  const goToMonth = React.useCallback(
    (next: Date) => {
      if (!controlled) setOwnMonth(next);
      onMonthChange?.(next);
    },
    [controlled, onMonthChange],
  );
  const [sel, setSel] = React.useState<Selection>(null);
  const [mode, setMode] = React.useState<SlotMode>("cards");

  // When the earliest slot lands (or changes), jump the view to its month. Only while uncontrolled:
  // once the caller drives the month, slots arriving for a month they paged away from must not drag
  // the view back to it.
  React.useEffect(() => {
    if (!controlled && earliest) setOwnMonth(startOfMonth(earliest));
  }, [earliest?.getTime(), controlled]); // eslint-disable-line react-hooks/exhaustive-deps

  // On month change, auto-select the first day in view that has open or booked slots.
  //
  // Only on a *change* of month. Slots arriving, or a caller re-rendering with a freshly filtered
  // array, must not reach in and re-pick the day: the invitee may have chosen a week since, and
  // silently swapping their selection back is what made the week arrows look stuck.
  const lastAutoSelect = React.useRef<{ monthKey: string; hadDays: boolean } | null>(null);
  React.useEffect(() => {
    const monthKey = `${viewMonth.getFullYear()}-${viewMonth.getMonth()}`;
    const monthKeys = [...new Set([...byDay.keys(), ...bookedByDay.keys(), ...blockedByDay.keys()])]
      .filter((k) => {
        const d = new Date(`${k}T00:00:00`);
        return d.getFullYear() === viewMonth.getFullYear() && d.getMonth() === viewMonth.getMonth();
      })
      .sort();
    // A month that came back empty is not settled: its slots may still be in flight, so the first
    // day is still owed once they land. Only a month already auto-selected *with* days in it is
    // left alone — that is the case where the invitee's own pick has to survive.
    const last = lastAutoSelect.current;
    if (last && last.monthKey === monthKey && last.hadDays) return;
    lastAutoSelect.current = { monthKey, hadDays: monthKeys.length > 0 };
    setSel(monthKeys.length ? { kind: "day", key: monthKeys[0] } : null);
  }, [viewMonth, byDay, bookedByDay, blockedByDay]);

  const weeks = React.useMemo(() => {
    const first = startOfMonth(viewMonth);
    const start = mondayOf(first);
    const out: Date[] = [];
    for (let w = 0; w < 6; w++) {
      const s = new Date(start);
      s.setDate(s.getDate() + w * 7);
      const last = new Date(s);
      last.setDate(last.getDate() + 6);
      // keep weeks that touch this month
      if (w > 0 && s.getMonth() !== viewMonth.getMonth() && last.getMonth() !== viewMonth.getMonth()) break;
      out.push(s);
    }
    return out;
  }, [viewMonth]);

  // Slots that sit outside the month on screen, so we can point invitees to them (they'd
  // otherwise have to guess and page around).
  const { beforeCount, afterCount, prevWithSlots, nextWithSlots } = React.useMemo(() => {
    const monthStart = startOfMonth(viewMonth).getTime();
    const nextMonthStart = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1).getTime();
    let before = 0;
    let after = 0;
    let latestBefore: string | null = null;
    let earliestAfter: string | null = null;
    for (const s of [...slots, ...bookedList]) {
      const t = new Date(s.start).getTime();
      if (t < monthStart) {
        before++;
        if (!latestBefore || s.start > latestBefore) latestBefore = s.start;
      } else if (t >= nextMonthStart) {
        after++;
        if (!earliestAfter || s.start < earliestAfter) earliestAfter = s.start;
      }
    }
    return {
      beforeCount: before,
      afterCount: after,
      prevWithSlots: latestBefore ? startOfMonth(new Date(latestBefore)) : null,
      nextWithSlots: earliestAfter ? startOfMonth(new Date(earliestAfter)) : null,
    };
  }, [slots, bookedList, viewMonth]);

  const monthTitle = viewMonth.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const todayKey = ymd(new Date());
  const jumpChip =
    "inline-flex items-center gap-1 rounded-full border border-teal-500/40 bg-teal-50 px-2.5 py-1 text-xs font-semibold text-teal-700 transition hover:bg-teal-100 dark:bg-teal-950/40 dark:text-teal-300 dark:hover:bg-teal-950/70";

  const pickDay = (key: string) => setSel({ kind: "day", key });
  const pickWeek = (monday: Date) => setSel({ kind: "week", mondayMs: monday.getTime() });

  // Friendly label for the current zone (city name from the list, else the IANA id).
  const zoneName = React.useMemo(() => {
    const found = ZONES.find((z) => z.id === tz);
    return found ? found.name : tz.replace(/_/g, " ");
  }, [tz]);
  // Picker list: the viewer's own zone first, then the curated set, filtered by the search box.
  const zoneOptions = React.useMemo(() => {
    let local: string | null = null;
    try {
      local = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      local = null;
    }
    const base = ZONES.slice();
    if (local && !base.some((z) => z.id === local)) base.unshift({ id: local, name: "Your timezone" });
    const q = tzQuery.trim().toLowerCase();
    return q
      ? base.filter((z) => `${z.name} ${z.id} ${zoneOffset(z.id)}`.toLowerCase().includes(q))
      : base;
  }, [tzQuery]);
  React.useEffect(() => {
    if (!tzOpen) return;
    const close = (): void => setTzOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [tzOpen]);

  return (
    <div className={cn("space-y-3", className)}>
      {allowTimezoneChange ? (
        <div className="relative flex justify-end">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setTzOpen((o) => !o);
            }}
            aria-expanded={tzOpen}
            className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--foreground)_/_0.28)] bg-background px-3 py-1.5 text-xs font-medium text-foreground transition hover:border-teal-500"
          >
            <Globe className="h-3.5 w-3.5 text-teal-600" />
            <span className="max-w-[52vw] truncate sm:max-w-none">{zoneName}</span>
            <span className="text-muted-foreground">{zoneOffset(tz)}</span>
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
          {tzOpen && (
            <div
              onClick={(e) => e.stopPropagation()}
              className="absolute right-0 top-10 z-30 w-80 max-w-[92vw] overflow-hidden rounded-xl border border-[hsl(var(--foreground)_/_0.2)] bg-card shadow-lg"
            >
              <div className="border-b border-border p-2">
                <input
                  autoFocus
                  value={tzQuery}
                  onChange={(e) => setTzQuery(e.target.value)}
                  placeholder="Search timezone"
                  className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>
              <ul className="max-h-72 overflow-auto p-1">
                {zoneOptions.map((z) => (
                  <li key={z.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setTz(z.id);
                        setTzOpen(false);
                        setTzQuery("");
                      }}
                      className={cn(
                        "flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-2 text-left text-[13px] transition hover:bg-muted",
                        z.id === tz && "bg-teal-50 font-semibold text-teal-800 dark:bg-teal-950/40 dark:text-teal-200",
                      )}
                    >
                      <span className="truncate">{z.name}</span>
                      <span className="shrink-0 text-xs text-muted-foreground">{zoneOffset(z.id)}</span>
                    </button>
                  </li>
                ))}
                {zoneOptions.length === 0 && (
                  <li className="px-2.5 py-3 text-center text-sm text-muted-foreground">No match</li>
                )}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <p className="flex items-center gap-1.5 text-xs font-medium text-foreground">
          <Globe className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          All times shown in your timezone: {zoneName} {zoneOffset(tz)}
        </p>
      )}
      {/* Wrap on the columns' OWN width, not the viewport's. A md: breakpoint here measured the
          window, so a narrow parent (the tokenised manage card) still got two columns and crushed
          the aspect-square date cells to ~18px. Below 536px the two columns stack instead. */}
      <div className="flex flex-wrap gap-4">
      {/* ---- month calendar ---- */}
      {/* Dimmed rather than blanked while a month loads: replacing the grid with a spinner makes
          every page step flash empty, which reads as "no availability" for the length of a request. */}
      <div
        className={cn(
          "min-w-[260px] flex-1 transition-opacity",
          // The builder preview gets a whole panel to itself, and an uncapped grid spent it on
          // cells big enough to look like a wall chart. Capped, the month reads at a glance and
          // the date fills the cell it sits in.
          compact && "max-w-[440px]",
          loadingMonth && "pointer-events-none opacity-50",
        )}
        aria-busy={loadingMonth || undefined}
      >
        <div className="mb-3 flex items-center justify-between">
          <span className="text-sm font-semibold">{monthTitle}</span>
          <div className="flex gap-1">
            {/* Disabled at the edge of the host's availability, with no explanation offered: the
                calendar simply stops, exactly as it would for a host with nothing more free. */}
            <button
              type="button"
              disabled={!canGoPrev}
              onClick={() => goToMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))}
              aria-label={beforeCount > 0 ? `Previous month (${beforeCount} more slots earlier)` : "Previous month"}
              className="relative grid h-7 w-7 place-items-center rounded-md border border-[hsl(var(--foreground)_/_0.28)] text-muted-foreground transition hover:border-teal-500 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:border-[hsl(var(--foreground)_/_0.28)] disabled:hover:text-muted-foreground"
            >
              <ChevronLeft className="h-4 w-4" />
              {beforeCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-teal-500 ring-2 ring-card" />
              )}
            </button>
            <button
              type="button"
              disabled={!canGoNext}
              onClick={() => goToMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))}
              aria-label={afterCount > 0 ? `Next month (${afterCount} more slots later)` : "Next month"}
              className="relative grid h-7 w-7 place-items-center rounded-md border border-[hsl(var(--foreground)_/_0.28)] text-muted-foreground transition hover:border-teal-500 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:border-[hsl(var(--foreground)_/_0.28)] disabled:hover:text-muted-foreground"
            >
              <ChevronRight className="h-4 w-4" />
              {afterCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-teal-500 ring-2 ring-card" />
              )}
            </button>
          </div>
        </div>

        <div className="mb-1.5 grid grid-cols-[26px_1fr] gap-1.5">
          <span />
          <div className="grid grid-cols-7 gap-1.5">
            {DOW_MINI.map((d, i) => (
              <span
                key={i}
                className="text-center text-[10px] font-bold uppercase tracking-wide text-foreground"
              >
                {d}
              </span>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          {weeks.map((weekStart) => {
            const weekSelected = sel?.kind === "week" && sel.mondayMs === weekStart.getTime();
            return (
              <div
                key={weekStart.getTime()}
                className={cn(
                  "grid grid-cols-[26px_1fr] gap-1.5 rounded-lg transition-colors",
                  // Selected via ‹›: wash the whole row so the week in context reads at a glance
                  // (the gaps between cells pick up the tint; the cells add their own below).
                  weekSelected && "bg-teal-500/5 dark:bg-teal-500/10",
                )}
              >
                <button
                  type="button"
                  onClick={() => pickWeek(weekStart)}
                  title="View this week"
                  aria-label={`View week of ${weekStart.toLocaleDateString()}`}
                  className={cn(
                    "grid place-items-center rounded-lg border text-[11px] transition",
                    weekSelected
                      ? "border-teal-500 bg-teal-50 text-teal-700 dark:bg-teal-950/40 dark:text-teal-300"
                      : "border-[hsl(var(--foreground)_/_0.28)] text-muted-foreground hover:border-teal-500 hover:text-teal-700",
                  )}
                >
                  ›
                </button>
                <div className="grid grid-cols-7 gap-1.5">
                  {Array.from({ length: 7 }, (_, k) => {
                    const day = new Date(weekStart);
                    day.setDate(day.getDate() + k);
                    const inMonth = day.getMonth() === viewMonth.getMonth();
                    const key = ymd(day);
                    const daySlots = inMonth ? byDay.get(key) ?? [] : [];
                    const dayBooked = inMonth ? bookedByDay.get(key) ?? [] : [];
                    const dayBlocked = inMonth ? blockedByDay.get(key) ?? [] : [];
                    const has = daySlots.length > 0 || dayBooked.length > 0 || dayBlocked.length > 0;
                    const daySelected = sel?.kind === "day" && sel.key === key;
                    const isWeekend = k >= 5;
                    return (
                      <button
                        key={k}
                        type="button"
                        disabled={!has}
                        onClick={() => has && pickDay(key)}
                        className={cn(
                          // grid place-items-center, not a flex column: the count and the dots are
                          // both absolutely placed, so the date is centred against the CELL and
                          // cannot be shifted by what else the day happens to hold. As a flex
                          // column the dots row and its gap added 10px of content, lifting the date
                          // 5px off centre on exactly the days that also carry a count.
                          "relative grid aspect-square place-items-center rounded-xl border transition",
                          !inMonth && "opacity-30",
                          // Border colour is chosen once (dark/sharp, or teal when selected) so
                          // there's no cascade fight between a base and an override.
                          daySelected
                            ? "border-teal-500 bg-teal-50 dark:bg-teal-950/40"
                            : has
                              ? cn(
                                  "cursor-pointer border-[hsl(var(--foreground)_/_0.28)] hover:border-teal-500 hover:bg-teal-50/60 dark:hover:bg-teal-950/30",
                                  isWeekend ? "bg-muted/50" : "bg-card",
                                )
                              : cn(
                                  "cursor-default border-[hsl(var(--foreground)_/_0.12)]",
                                  isWeekend ? "bg-muted/50" : "bg-card",
                                ),
                          // Tint each cell of the selected week (twMerge lets this win over the
                          // bg above) so the row reads as one continuous hued band.
                          weekSelected && !daySelected && "bg-teal-500/10 dark:bg-teal-500/15",
                        )}
                      >
                        <span
                          className={cn(
                            "font-semibold tabular-nums",
                            // The preview's cells are far roomier than the invitee link's, and the
                            // same 15px date left them looking empty. Sized to the cell it is in.
                            // Below ~375px the cell is too small to hold a two-digit date and a
                            // corner count at full size without them touching, so both step down
                            // together. Above it nothing changes.
                            compact ? "text-[18px]" : "text-[15px] max-[380px]:text-[13px] max-[340px]:text-[12px]",
                            // AFTER the size, deliberately: twMerge files text-[size] in the
                            // font-size group, which overrides line-height, so a leading-* written
                            // before it is silently dropped. Without it the date's line box is 1.5x
                            // its own height and reaches up into the count's corner.
                            "leading-none",
                            // A day with nothing on is still a date to read, not disabled chrome
                            // (principle 27): dimmed, but not the grey reserved for metadata.
                            has ? "text-foreground" : "text-foreground/60",
                            key === todayKey && "text-teal-700 dark:text-teal-300",
                          )}
                        >
                          {day.getDate()}
                        </span>
                        {has && (
                          <>
                            {/* Corner-anchored, on its own line box, and inset off the ROUNDED
                                corner rather than off the straight edges. A cell is rounded-xl, so
                                a box at (4px, 0px) has its top right corner outside the 12px arc:
                                at (12-dx)^2 + (12-dy)^2 = 208 > 144 the glyph was sitting on the
                                curve, which is why it read as touching the edge. At (5px, 3px) the
                                ink clears the arc by ~1.8px while its centre stays deep in the top
                                right quadrant, so it still reads as pinned to the corner.
                                The inset is on right/top only, so a two- or three-digit count grows
                                LEFTWARDS and keeps exactly this clearance; its 8px line box ends
                                above the centred date's, so it never eats into the date either. */}
                            <span className="absolute right-[5px] top-[3px] text-[8px] font-bold leading-none tabular-nums max-[380px]:top-[2px] max-[380px]:text-[7px]">
                              {daySlots.length > 0 ? (
                                <span className="text-teal-700 dark:text-teal-300">{daySlots.length}</span>
                              ) : dayBooked.length > 0 ? (
                                <span className="text-amber-600 dark:text-amber-400">{dayBooked.length}</span>
                              ) : (
                                <span className="text-brand-contrast">{dayBlocked.length}</span>
                              )}
                            </span>
                            {/* Pinned to the floor of the cell and out of the date's way: smaller,
                                darker and tighter than before, so seven of them still fit a cell
                                the old 4px dots overflowed. */}
                            <span className="absolute inset-x-0 bottom-[3px] flex items-center justify-center gap-[2px]">
                              {daySlots.slice(0, 3).map((s) => (
                                <span key={s.start} className="h-[3px] w-[3px] rounded-full bg-teal-600 dark:bg-teal-400" />
                              ))}
                              {dayBooked.slice(0, 2).map((s) => (
                                <span key={`b-${s.start}`} className="h-[3px] w-[3px] rounded-full bg-amber-600 dark:bg-amber-400" />
                              ))}
                              {dayBlocked.slice(0, 2).map((s) => (
                                <span key={`x-${s.start}`} className="h-[3px] w-[3px] rounded-full bg-brand-contrast" />
                              ))}
                            </span>
                          </>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {(beforeCount > 0 || afterCount > 0) && (
          <div className="mt-3 flex flex-wrap gap-2">
            {prevWithSlots && (
              <button type="button" className={jumpChip} onClick={() => goToMonth(prevWithSlots)}>
                <ChevronLeft className="h-3.5 w-3.5" />
                {beforeCount} more in {prevWithSlots.toLocaleDateString(undefined, { month: "short" })}
              </button>
            )}
            {nextWithSlots && (
              <button type="button" className={jumpChip} onClick={() => goToMonth(nextWithSlots)}>
                {afterCount} more in {nextWithSlots.toLocaleDateString(undefined, { month: "short" })}
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* ---- slots focus ---- */}
      <div className="min-w-[260px] flex-1">
        <FocusPanel
          sel={sel}
          byDay={byDay}
          bookedByDay={bookedByDay}
          blockedByDay={blockedByDay}
          slotMinutes={slotMinutes}
          tz={tz}
          mode={mode}
          onMode={setMode}
          readOnly={readOnly}
          onPick={onPick}
          compact={!!compact}
          hasAnySlots={slots.length > 0 || bookedList.length > 0 || blockedList.length > 0}
        />
      </div>
      </div>
    </div>
  );
}

function FocusPanel({
  sel,
  byDay,
  bookedByDay,
  blockedByDay,
  slotMinutes,
  tz,
  mode,
  onMode,
  readOnly,
  onPick,
  compact,
  hasAnySlots,
}: {
  sel: Selection;
  byDay: Map<string, CalendarSlot[]>;
  bookedByDay: Map<string, BookedSlot[]>;
  blockedByDay: Map<string, BlockedSlot[]>;
  slotMinutes: number;
  tz: string;
  mode: SlotMode;
  onMode: (m: SlotMode) => void;
  readOnly: boolean;
  onPick?: (slot: CalendarSlot) => void;
  compact: boolean;
  hasAnySlots: boolean;
}) {
  if (!hasAnySlots) {
    return (
      <div className="grid min-h-[240px] place-items-center rounded-xl border border-dashed border-[hsl(var(--foreground)_/_0.28)] p-6 text-center text-sm text-muted-foreground">
        No open times right now.
      </div>
    );
  }
  if (!sel) {
    return (
      <div className="grid min-h-[240px] place-items-center rounded-xl border border-dashed border-[hsl(var(--foreground)_/_0.28)] p-6 text-center">
        <div className="space-y-2 text-muted-foreground">
          <CalendarRange className="mx-auto h-7 w-7 opacity-60" />
          <p className="text-sm">Pick a day, or a week's ›, to see open times.</p>
        </div>
      </div>
    );
  }

  let title: string;
  let sub: string;
  let content: React.ReactNode;

  // Builder preview shrinks the pills; the public invitee pages keep the larger, tappable size.
  const pad = compact ? "px-1.5 py-1 text-[11px]" : "px-2.5 py-2 text-[13px]";

  const chip = (s: CalendarSlot, extraClass?: string) =>
    readOnly ? (
      <span
        key={s.start}
        className={cn(
          "rounded-lg border border-[hsl(var(--foreground)_/_0.28)] bg-muted/40 text-center font-semibold tabular-nums text-foreground",
          pad,
          extraClass,
        )}
      >
        {fmtTime(s.start, tz)}
      </span>
    ) : (
      <button
        key={s.start}
        type="button"
        onClick={() => onPick?.(s)}
        className={cn(
          "rounded-lg border border-[hsl(var(--foreground)_/_0.3)] bg-background text-center font-semibold tabular-nums transition hover:border-teal-500 hover:bg-teal-100 dark:hover:bg-teal-950/40",
          pad,
          extraClass,
        )}
      >
        {fmtTime(s.start, tz)}
      </button>
    );

  // A taken slot on the host's calendar — amber, never clickable, with the invitee for context.
  const bookedChip = (s: BookedSlot, extraClass?: string) => (
    <span
      key={`b-${s.start}`}
      title={s.label ? `Booked: ${s.label}` : "Booked"}
      className={cn(
        "flex flex-col rounded-lg border border-amber-500/40 bg-amber-50 text-center font-semibold tabular-nums text-amber-800 dark:bg-amber-950/30 dark:text-amber-300",
        pad,
        extraClass,
      )}
    >
      <span>{fmtTime(s.start, tz)}</span>
      {s.label && <span className="truncate text-[10px] font-medium opacity-80">{s.label}</span>}
    </span>
  );

  // A slot the preview dropped for clashing with a meeting — brand-contrast, never clickable, struck
  // through, with the overlapping meeting's time in the tooltip. Builder preview only.
  const blockedChip = (s: BlockedSlot, extraClass?: string) => (
    <span
      key={`x-${s.start}`}
      title={s.conflictAt ? `Overlapping meeting at ${fmtTime(s.conflictAt, tz)}` : "Overlapping meeting"}
      className={cn(
        "rounded-lg border border-brand-contrast/40 bg-brand-contrast/10 text-center font-semibold tabular-nums text-brand-contrast",
        pad,
        extraClass,
      )}
    >
      <span className="line-through">{fmtTime(s.start, tz)}</span>
    </span>
  );

  if (sel.kind === "day") {
    const day = new Date(`${sel.key}T00:00:00`);
    const daySlots = byDay.get(sel.key) ?? [];
    const dayBooked = bookedByDay.get(sel.key) ?? [];
    const dayBlocked = blockedByDay.get(sel.key) ?? [];
    title = fmtDayLong(day);
    sub =
      `${daySlots.length} open · ${dayBooked.length} booked` +
      (dayBlocked.length > 0 ? ` · ${dayBlocked.length} blocked` : "") +
      ` · ${slotMinutes} min each`;
    content =
      mode === "cards" ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(78px,1fr))] gap-2">
          {daySlots.map((s) => chip(s))}
          {dayBooked.map((s) => bookedChip(s))}
          {dayBlocked.map((s) => blockedChip(s))}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {daySlots.map((s) => chip(s, "!text-left px-3"))}
          {dayBooked.map((s) => bookedChip(s, "!text-left px-3"))}
          {dayBlocked.map((s) => blockedChip(s, "!text-left px-3"))}
        </div>
      );
  } else {
    const monday = new Date(sel.mondayMs);
    const days = Array.from({ length: 7 }, (_, k) => {
      const d = new Date(monday);
      d.setDate(d.getDate() + k);
      return {
        d,
        slots: byDay.get(ymd(d)) ?? [],
        booked: bookedByDay.get(ymd(d)) ?? [],
        blocked: blockedByDay.get(ymd(d)) ?? [],
      };
    });
    const total = days.reduce((n, x) => n + x.slots.length, 0);
    const bookedTotal = days.reduce((n, x) => n + x.booked.length, 0);
    const blockedTotal = days.reduce((n, x) => n + x.blocked.length, 0);
    title = `Week of ${monday.toLocaleDateString(undefined, { day: "numeric", month: "long" })}`;
    sub = `${total} open · ${bookedTotal} booked this week`;
    content =
      mode === "weeks" ? (
        <div className="grid grid-cols-7 gap-2">
          {days.map(({ d, slots, booked, blocked }, k) => (
            <div key={k} className="min-w-0">
              <h4 className="mb-2 text-center text-[10px] font-bold uppercase tracking-wide text-foreground">
                {DOW_SHORT[k]} {d.getDate()}
              </h4>
              <div className="flex flex-col gap-1.5">
                {slots.length || booked.length || blocked.length ? (
                  <>
                    {slots.map((s) => chip(s, "!px-1 !text-[12px]"))}
                    {booked.map((s) => bookedChip(s, "!px-1 !text-[12px]"))}
                    {blocked.map((s) => blockedChip(s, "!px-1 !text-[12px]"))}
                  </>
                ) : (
                  <span className="text-center text-[11px] text-muted-foreground">—</span>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {days
            .filter((x) => x.slots.length || x.booked.length || x.blocked.length)
            .map(({ d, slots, booked, blocked }) => (
              <div key={d.getTime()}>
                <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-foreground">
                  {d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" })}
                </h4>
                <div className="grid grid-cols-[repeat(auto-fill,minmax(78px,1fr))] gap-2">
                  {slots.map((s) => chip(s))}
                  {booked.map((s) => bookedChip(s))}
                  {blocked.map((s) => blockedChip(s))}
                </div>
              </div>
            ))}
          {total === 0 && bookedTotal === 0 && blockedTotal === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">Nothing this week.</p>
          )}
        </div>
      );
  }

  const isWeek = sel.kind === "week";

  return (
    <div>
      {/* In week context (a ‹› pick, or the Weeks grid) the header carries a teal wash + left rail so
          the switch of scope from a single day to a whole week is unmistakable. */}
      <div
        className={cn(
          "mb-4 rounded-lg transition-colors",
          isWeek && "border-l-2 border-teal-500 bg-teal-500/5 py-2 pl-3 pr-2 dark:bg-teal-500/10",
        )}
      >
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-base font-semibold">{title}</h3>
          <div className="inline-flex overflow-hidden rounded-lg border border-[hsl(var(--foreground)_/_0.28)] text-xs font-semibold">
            {(["cards", "weeks"] as SlotMode[]).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => onMode(m)}
                className={cn(
                  "px-3 py-1.5 capitalize transition",
                  mode === m
                    ? "bg-teal-50 text-teal-700 dark:bg-teal-950/40 dark:text-teal-300"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
        <p className="text-[13px] font-medium text-foreground">{sub}</p>
      </div>
      {content}
    </div>
  );
}
